import TaskResultLimitPanel from "Areas/Documents/documentDetails/taskResultLimitPanel";
import AddToSummaryReport, { NEW_SUMMARY_REPORT_OBJECT } from "Areas/SummaryReports/addToSummaryReport";
import { LAYOUT_ALGORITHM, layoutAlgorithmLocale } from "Common/dictionaries/layoutAlgorithm";
import { SUMMARY_REPORT_OBJECT_TYPE } from "Common/dictionaries/summaryReportObjectType";
import { formatFractionToPercent } from "Common/digitUtils/format";
import { RedoButton, RefreshButton, SaveButton, UndoButton } from "Components/button";
import ButtonGroup from "Components/buttonGroup";
import CheckRadioButton from "Components/checkBoxRadio";
import ScrolledDropdown from "Components/scrolledDropdown";
import classNames from "classnames";
import find from "lodash/find";
import map from "lodash/map";
import React, { memo, useCallback, useId, useMemo, useState } from "react";
import { ZOOM, redoButtonAttrs, undoButtonAttrs } from "../constants";
import locale from "./graphPanel.locale";
import ColorStyledBlock from "Components/colorStyledBlock";
import styles from "./graphPanel.module.css";
import { Divider } from "Components/Divider";
import { ButtonWithDocumentPanel } from "Areas/SharedFeatures/downSidePanel";
import { Button } from "UiKit";
const SIZES = {
    BTN_SIZE: 32,
    BTN_GROUP_HEIGHT: 28,
    BTN_DOCS: 32
};
const keySelector = (e) => e.key;
const textSelector = (e) => e.text;
const zoomValues = [ZOOM.DEFAULT];
const step = 0.5;
while (zoomValues[zoomValues.length - 1] + step < ZOOM.MAX)
    zoomValues.push(zoomValues[zoomValues.length - 1] + step);
zoomValues.push(ZOOM.MAX);
while (zoomValues[0] - step > ZOOM.MIN)
    zoomValues.unshift(zoomValues[0] - step);
zoomValues.unshift(ZOOM.MIN);
const zoomItems = map(zoomValues, item => ({
    key: item,
    text: formatFractionToPercent(item, 0)
}));
const layoutItems = map(LAYOUT_ALGORITHM, item => ({
    key: item,
    text: layoutAlgorithmLocale[item]
}));
const allScreenRadioItem = {
    Key: locale.ALL_SCREEN,
    Name: locale.ALL_SCREEN
};
const currentScreenRadioItem = {
    Key: locale.CURRENT_SCREEN,
    Name: locale.CURRENT_SCREEN
};
function GraphPanel(props) {
    const [summaryReportObjectType, setSummaryReportObjectType] = useState(SUMMARY_REPORT_OBJECT_TYPE.GRAPH_SCREEN);
    const [allScreen, setAllScreen] = useState(false);
    const panelId = useId();
    const layoutAlgorithmTogglerContent = useMemo(() => {
        var _a;
        const layoutAlgorithmText = (_a = find(layoutItems, ["key", props.layoutAlgorithm])) === null || _a === void 0 ? void 0 : _a.text;
        return (React.createElement("span", { className: "grid-cols-auto no-gap" },
            React.createElement("i", { className: "icon-layout-algorithm font-h5" }),
            layoutAlgorithmText && React.createElement("span", { className: "ml-0_5 font-small" }, layoutAlgorithmText)));
    }, [props.layoutAlgorithm]);
    const summaryReportObject = useMemo(() => ({
        ...NEW_SUMMARY_REPORT_OBJECT,
        DocumentId: props.documentId,
        SummaryReportObjectType: summaryReportObjectType
    }), [props.documentId, summaryReportObjectType]);
    const addToSummaryReportFormTabs = useMemo(() => [
        {
            key: SUMMARY_REPORT_OBJECT_TYPE.GRAPH_SCREEN,
            text: locale.SCREEN
        },
        {
            key: SUMMARY_REPORT_OBJECT_TYPE.DOCUMENT_OBJECT_NODES,
            element: (React.createElement("span", { className: "grid-cols-auto with-gap-xxs justify-content-center" },
                locale.OBJECT,
                Boolean(props.selectedCount) && (React.createElement("div", { className: "bg-light-surface font-small pl-xxs pr-xxs text-primary-500 border-rounded-lg" }, props.selectedCount))))
        }
    ], [props.selectedCount]);
    const AddToSummaryReportCustomForm = useMemo(() => (React.createElement("div", null,
        React.createElement("div", { className: "font-smallest text-gray-500 pb-1 pt-1_5" }, locale.FORMAT),
        React.createElement("div", { className: "position-relative" },
            React.createElement(ButtonGroup, { height: SIZES.BTN_GROUP_HEIGHT, items: addToSummaryReportFormTabs, selectedItemKey: summaryReportObjectType, onItemClick: (itemData) => {
                    if (itemData.key !== summaryReportObjectType)
                        setSummaryReportObjectType(itemData.key);
                } })),
        !props.selectedCount &&
            summaryReportObjectType === SUMMARY_REPORT_OBJECT_TYPE.DOCUMENT_OBJECT_NODES && (React.createElement(ColorStyledBlock, { color: "error-message-color", bg: "error-message-bg", border: "error-message-border", className: "p-0_5 mt-0_75 font-small overflow-hidden line-height-small", height: 56 },
            React.createElement("i", { className: "icon-info-circle-filled line-height-1 mr-0_25 font-h4" }),
            locale.NO_SELECTION_HINT)),
        summaryReportObjectType === SUMMARY_REPORT_OBJECT_TYPE.GRAPH_SCREEN && (React.createElement("div", { className: "grid-cols-auto pt-0_5" },
            React.createElement(CheckRadioButton, { item: currentScreenRadioItem, onCheckedChanged: () => {
                    setAllScreen(false);
                }, checked: !allScreen }),
            React.createElement(CheckRadioButton, { item: allScreenRadioItem, onCheckedChanged: () => {
                    setAllScreen(true);
                }, checked: allScreen }))))), [summaryReportObjectType, allScreen, addToSummaryReportFormTabs, props.selectedCount]);
    const handleAddToSummaryReportFormHiding = useCallback(() => {
        setSummaryReportObjectType(SUMMARY_REPORT_OBJECT_TYPE.GRAPH_SCREEN);
        setAllScreen(false);
    }, []);
    const handleSummaryReportObjectValue = useCallback(() => props.getSummaryReportObjectValue(summaryReportObjectType, allScreen), [props.getSummaryReportObjectValue, summaryReportObjectType, allScreen]);
    return (React.createElement("div", { className: classNames(styles.wrapper, props.rightPanelOpen && styles["right-margin"]) },
        React.createElement("div", { className: classNames(styles.container, props.className), id: panelId },
            React.createElement("div", { className: styles.section },
                React.createElement(ScrolledDropdown, { keySelector: keySelector, textSelector: textSelector, selectedKey: props.layoutAlgorithm, onSelectedChanged: props.onChangeLayoutAlgorithm, items: layoutItems, togglerBtnContent: layoutAlgorithmTogglerContent, togglerClassName: "no-border btn-filled btn-with-icon pt-0 pb-0", togglerHeight: SIZES.BTN_SIZE, maxHeight: 300, minWidth: 90, disabled: !props.onChangeLayoutAlgorithm })),
            React.createElement(Divider, { direction: "vertical" }),
            React.createElement("div", { className: styles.section },
                React.createElement(UndoButton, { disabled: !props.canUndoOrRedo, elementAttr: undoButtonAttrs, useNewBtn: true }),
                React.createElement(RedoButton, { disabled: !props.canUndoOrRedo, elementAttr: redoButtonAttrs, useNewBtn: true })),
            React.createElement(Divider, { direction: "vertical" }),
            React.createElement("div", { className: styles.section },
                React.createElement(SaveButton, { onSave: props.onSave, disabled: !props.onSave, useNewBtn: true }),
                React.createElement(RefreshButton, { onRefresh: props.onRefresh, disabled: !props.onRefresh, useNewBtn: true, needConfirm: Boolean(props.onSave) })),
            React.createElement(Divider, { direction: "vertical" }),
            React.createElement("div", { className: styles.section },
                React.createElement(Button, { styleType: "text", size: "32", onClick: props.onCopy || props.onPaste, disabled: !(props.onCopy || props.onPaste), tooltipMessage: props.onCopy || !props.onPaste ? locale.COPY : locale.PASTE, iconOnly: true },
                    React.createElement("i", { className: "icon-document-clone font-h5" })),
                React.createElement(Button, { styleType: "text", size: "32", onClick: props.onDelete, disabled: !props.onDelete, tooltipMessage: locale.DELETE, iconOnly: true },
                    React.createElement("i", { className: "icon-trash font-h5" }))),
            React.createElement(Divider, { direction: "vertical" }),
            React.createElement("div", { className: styles.section },
                React.createElement(TaskResultLimitPanel, null)),
            props.projectId && (React.createElement(React.Fragment, null,
                React.createElement(Divider, { direction: "vertical" }),
                React.createElement("div", { className: styles.section },
                    React.createElement(AddToSummaryReport, { height: SIZES.BTN_SIZE, disabled: !props.canAddToSummaryReports, data: summaryReportObject, projectId: props.projectId, getValue: handleSummaryReportObjectValue, customForm: AddToSummaryReportCustomForm, onHiding: handleAddToSummaryReportFormHiding })))),
            React.createElement(Divider, { direction: "vertical" }),
            React.createElement("div", { className: styles.section },
                React.createElement(ScrolledDropdown, { keySelector: keySelector, textSelector: textSelector, selectedKey: props.zoom, onSelectedChanged: props.onChangeZoom, items: zoomItems, togglerClassName: classNames(styles.dropdown, "no-border btn-filled pt-0 pb-0"), togglerHeight: SIZES.BTN_SIZE, minWidth: 75, maxHeight: 200, caption: formatFractionToPercent(props.zoom, 0), togglerTitle: locale.ZOOM, togglerMuteDefaultCaptionText: false })),
            React.createElement(Divider, { direction: "vertical" }),
            React.createElement("div", { className: styles.section },
                React.createElement(ButtonWithDocumentPanel, { targetId: panelId })))));
}
export default memo(GraphPanel);
